import { SuiteAppProps } from '../SuiteDropdown/types';
import {
  chakraTheme,
  LaunchDarklyFeatureFlagsProps,
  WebinarIcon,
  DataIcon,
  CommunityIcon,
  ProviderPerformanceIcon,
  ClassicIcon,
  LockIcon,
  LogoutIcon,
  CareRetentionIcon,
  RiskContractIcon,
  CareAcceleratorIcon,
  ArcadiaMenuIcon,
  CJLogoIcon
} from 'cj-common-react';
import mixpanel from 'mixpanel-browser';
import { getTenantOrigin } from 'util/tenantUrl';
import { User } from '@auth0/auth0-react';
import { env, HOME_ENVIRONMENT } from 'config/environments/homeEnvironments';
import { History } from 'history';
import { userHasPermission } from 'pages/Home/config';

export const communityURL =
  env === 'prod'
    ? 'https://dataverse.carejourney.com/community'
    : 'https://dataverse-int.carejourney.com/community';

export const arcadiaCommunityURL = 'https://community.arcadia.io/carejourney-community';

export const webinarsURL = 'https://dc.carejourney.com/member-webinars';

export const arcadiaWebinarsURL = 'https://community.arcadia.io/member-webinars';

export const getHomeApps = (
  user: User | undefined,
  history: History,
  featureFlags: LaunchDarklyFeatureFlagsProps
): SuiteAppProps[] => {

  const activeTextColor = featureFlags?.useNewArcadiaLogoAndColors ? '#7400F8' : chakraTheme?.colors?.blueOne;

  const apps = [
    {
      name: 'home',
      label: featureFlags.showNewBrandUi
        ? 'CareJourney by Arcadia Home'
        : 'CareJourney Home',
      icon: (active) =>
        featureFlags?.useNewArcadiaLogoAndColors
          ? (
            <ArcadiaMenuIcon
              width={24}
              height={24}
              color={
                active
                  ? activeTextColor
                  : chakraTheme.colors?.mediumGrey
              }
              cursor='pointer'
            />)
          : (
            <CJLogoIcon
              width={23}
              height={19}
              color={
                active
                  ? activeTextColor
                  : chakraTheme.colors?.mediumGrey
              }
              cursor='pointer'
            />),
      handleClick: () => {
        mixpanel.track('home Clicked', {
          appName: 'home'
        });
        history.push('/home');
      }
    }
  ];

  if (
    featureFlags?.showProviderPerformanceTile &&
    userHasPermission(user || {}, 'providerPerformance')
  ) {
    apps.push({
      name: 'providerPerformance',
      label: 'Provider Performance',
      icon: (active) => (
        <ProviderPerformanceIcon
          variant='outlined'
          height={23}
          width={23}
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          cursor='pointer'
        />
      ),
      handleClick: () => {
        mixpanel.track('Provider Performance Clicked', {
          appName: 'Provider Performance'
        });
        history.push(
          featureFlags.showNewProviderAdvantage
            ? '/provider-performance-v2'
            : '/provider-performance'
        );
      }
    });
  }

  if (
    !featureFlags?.hideContentForCjTrialMembers &&
    featureFlags?.showCareRetentionTile &&
    userHasPermission(user || {}, 'careRetention')
  ) {
    apps.push({
      name: 'careRetention',
      label: 'Care Retention',
      icon: (active) => (
        <CareRetentionIcon
          variant='outlined'
          height={23}
          width={23}
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          cursor='pointer'
        />
      ),
      handleClick: () => {
        mixpanel.track('Care Retention Clicked', {
          appName: 'Care Retention'
        });
        history.push('/care-retention');
      }
    });
  }

  if (
    featureFlags?.showRiskContractManagementTile &&
    userHasPermission(user || {}, 'riskContractManagement')
  ) {
    apps.push({
      name: 'riskContractManagement',
      label: 'Risk Management',
      icon: (active) => (
        <RiskContractIcon
          variant='outlined'
          height={30}
          width={30}
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          cursor='pointer'
        />
      ),
      handleClick: () => {
        mixpanel.track('Risk Contract Management Clicked', {
          appName: 'Risk Contract Management'
        });
        history.push('/risk-management');
      }
    });
  }

  if (featureFlags?.showCarejourneyClassicTile) {
    apps.push({
      name: 'cjClassic',
      label: 'CareJourney Classic',
      icon: (active) => (
        <ClassicIcon
          variant='outlined'
          height={23}
          width={23}
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          cursor='pointer'
        />
      ),
      handleClick: () => {
        mixpanel.track('Care Journey Classic App Clicked', {
          appName: 'carejouneyLegacy'
        });
        window.location.href = HOME_ENVIRONMENT.PORTAL_URL;
      }
    });
  }

  if (
    featureFlags.showCareAccelerator &&
    userHasPermission(user || {}, 'careAccelerator')
  ) {
    apps.push({
      name: 'careAccelerator',
      label: 'Patient Pulse',
      icon: (active) => (
        <CareAcceleratorIcon
          height={23}
          width={23}
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          variant='outlined'
          cursor='pointer'
        />
      ),
      handleClick: () => {
        mixpanel.track('Care Accelertor App Clicked', {
          appName: 'careAccelerator'
        });
        history.push('/care-accelerator');
      }
    });
  }

  if (featureFlags?.showDataCommonsTiles) {
    apps.push({
      name: 'webinars',
      label: 'Webinars',
      icon: (active) => (
        <WebinarIcon
          variant='outlined'
          height={23}
          width={23}
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          cursor='pointer'
        />
      ),
      handleClick: () => {
        mixpanel.track('webinars Clicked', {
          appName: 'webinars'
        });
        window.location.href = featureFlags?.useArcadiaCommunityLinks ? arcadiaWebinarsURL : webinarsURL;
      }
    });
  }

  if (featureFlags?.showCommunityTile) {
    apps.push({
      name: 'community',
      label: 'Community',
      icon: (active) => (
        <CommunityIcon
          variant='outlined'
          height={23}
          width={23}
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          cursor='pointer'
        />
      ),
      handleClick: () => {
        mixpanel.track('community Clicked', {
          appName: 'community'
        });
        window.location.href = featureFlags?.useArcadiaCommunityLinks ? arcadiaCommunityURL : communityURL;
      }
    });
  }

  if (featureFlags?.showDataCommonsTiles) {
    apps.push({
      name: 'dataVerse',
      label: 'Dataverse',
      icon: (active) => (
        <DataIcon
          variant='outlined'
          height={23}
          width={23}
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          cursor='pointer'
        />
      ),
      handleClick: () => {
        mixpanel.track('dataverse Clicked', {
          appName: 'dataverse'
        });
        history.push('/dataverse');
      }
    });
  }

  return apps;
};

export const getUserMenuDropdownItems = (
  user: User | undefined,
  logout: Function,
  setIsChangePasswordModalOpen: Function,
  featureFlags: LaunchDarklyFeatureFlagsProps
): SuiteAppProps[] => {
  const userMenuApss: SuiteAppProps[] = [];
  const isCareJourneyEmployee =
    user?.email && user.email.endsWith('@carejourney.com');

  const activeTextColor = featureFlags?.useNewArcadiaLogoAndColors ? '#7400F8' : chakraTheme?.colors?.blueOne;

  if (!isCareJourneyEmployee) {
    userMenuApss.push({
      name: 'changePassword',
      label: 'Change Password',
      icon: (active) => (
        <LockIcon
          color={
            active
              ? activeTextColor
              : chakraTheme.colors?.mediumGrey
          }
          width={20}
          height={20}
          cursor='pointer'
        />
      ),
      handleClick: () => setIsChangePasswordModalOpen(true)
    });
  }

  userMenuApss.push({
    name: 'signOut',
    label: 'Sign Out',
    labelColor: chakraTheme.colors?.red,
    activeBorderColor: chakraTheme.colors?.red,
    icon: () => (
      <LogoutIcon
        color={chakraTheme.colors?.red}
        width={20}
        height={20}
        cursor='pointer'
      />
    ),
    handleClick: () => {
      mixpanel.track('Sign out', {
        currentPage: window.location.pathname
      });
      mixpanel.reset();
      window.open(
        'https://dataverse.carejourney.com/_hcms/mem/logout/',
        '_blank'
      );
      logout({
        returnTo: getTenantOrigin()
      });
    }
  });

  return userMenuApss;
};
